import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import {
  errorText,
  formatHash,
  formatError,
  result,
  validation,
  validationError,
} from './shared.ts';

async function collect(): Promise<RawData> {
  const html = {
    dialog: 'HTMLDialogElement' in window,
    popover: 'popover' in HTMLElement.prototype,
    details: 'HTMLDetailsElement' in window,
    template: 'HTMLTemplateElement' in window,
  };
  const css = {
    has: CSS.supports('selector(:has(*))'),
    containerQueries: CSS.supports('container-type: inline-size'),
    colorMix: CSS.supports('color', 'color-mix(in srgb, red, blue)'),
  };
  const graphics = {
    webgl: Boolean(document.createElement('canvas').getContext('webgl')),
    webgl2: Boolean(document.createElement('canvas').getContext('webgl2')),
    webgpu: 'gpu' in navigator,
    offscreenCanvas: 'OffscreenCanvas' in window,
  };
  const media = {
    mediaDevices: 'mediaDevices' in navigator,
    mediaCapabilities: 'mediaCapabilities' in navigator,
    mediaSession: 'mediaSession' in navigator,
    pictureInPicture: 'pictureInPictureEnabled' in document,
  };
  const storage = {
    indexedDb: 'indexedDB' in window,
    caches: 'caches' in window,
    storageManager: 'storage' in navigator,
    cookieStore: 'cookieStore' in window,
  };
  const network = {
    webSocket: 'WebSocket' in window,
    webTransport: 'WebTransport' in window,
    webRtc: 'RTCPeerConnection' in window,
    broadcastChannel: 'BroadcastChannel' in window,
  };
  return { html, css, graphics, media, storage, network };
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return formatHash(rawData);
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validation([]);
}

export const featureInventoryTest: TestDefinition = {
  id: 'browserFeatures',
  title: 'Browser features',
  category: 'shared',
  format,
  run,
  validate,
};
